import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GeoLocation, LocationDiagnostics } from '../types';
import { chargerService } from '../services/chargerService';
import { queueAccessService } from '../services/queueAccessService';
import {
  canOpenTelegramLocationSettings,
  getCurrentLocation,
  LocationError,
  LocationErrorCode,
  openTelegramLocationSettings,
} from '../utils/location';
import { CaptchaModal } from '../components/captcha/CaptchaModal';
import { LocationCheckModal } from '../components/LocationCheckModal';

/**
 * Error shaped like an axios error, so existing join error handlers
 * (which read error.response.data.message) display it unchanged.
 */
export class QueueAccessError extends Error {
  response: { data: { message: string; code: string } };

  constructor(public code: string, message: string) {
    super(message);
    this.name = 'QueueAccessError';
    this.response = { data: { message, code } };
  }
}

interface PendingCaptcha {
  resolve: (passToken: string) => void;
  reject: (error: Error) => void;
}

/** What the location pop-up shows: a location that failed the check, or no location at all. */
export type LocationIssue =
  | { kind: 'failed'; diagnostics: LocationDiagnostics; retrying: boolean }
  | { kind: 'unavailable'; reason: LocationErrorCode; canOpenSettings: boolean; retrying: boolean };

const errorCode = (error: unknown): LocationErrorCode => (error instanceof LocationError ? error.code : 'unavailable');

/**
 * Joins a queue under the anti-bot rules: gets the device location for Telegram users,
 * asks the server what is required, shows the captcha when needed, then joins. When the
 * location check fails, a pop-up shows what was detected and lets the user retry.
 * Render `modals` somewhere in the calling component.
 */
export function useQueueJoinFlow() {
  const { t } = useTranslation();
  const [pendingCaptcha, setPendingCaptcha] = useState<PendingCaptcha | null>(null);
  const [locationIssue, setLocationIssue] = useState<LocationIssue | null>(null);
  const locationDecision = useRef<{ resolve: () => void; reject: (error: Error) => void } | null>(null);

  const messageFor = useCallback(
    (code: string | null, serverMessage?: string | null) =>
      serverMessage || t(`queueAccess.codes.${code}`, { defaultValue: t('errors.generic') }),
    [t]
  );

  const solveCaptcha = useCallback(
    () =>
      new Promise<string>((resolve, reject) => setPendingCaptcha({ resolve, reject })),
    []
  );

  /** Shows the location pop-up; resolves when the user taps "Try again", rejects when they close it. */
  const askAboutLocation = useCallback(
    (issue: LocationIssue) =>
      new Promise<void>((resolve, reject) => {
        locationDecision.current = { resolve, reject };
        setLocationIssue(issue);
      }),
    []
  );

  const joinQueue = useCallback(
    async (chargerId: string, shift?: number) => {
      let access = await queueAccessService.check(shift);
      let location: GeoLocation | null = null;

      // Only shift 1 can need a location: Telegram users outside the "any location" hours must be
      // at an allowed place (while admins have the location check on). The server says so with
      // code location_required. Shifts 2 and 3 and web users never ask for it.
      // Keep showing the location pop-up until it passes or the user gives up.
      if (shift === 1 && access.isTelegramUser && access.code === 'location_required') {
        for (;;) {
          let failure: unknown = null;
          try {
            location = await getCurrentLocation();
          } catch (error) {
            failure = error;
            location = null;
          }
          // The server also logs why no location arrived
          access = await queueAccessService.check(shift, location, failure ? errorCode(failure) : undefined);

          let issue: LocationIssue | null = null;
          if (failure) {
            issue = {
              kind: 'unavailable',
              reason: errorCode(failure),
              canOpenSettings: failure instanceof LocationError && failure.fromTelegram && canOpenTelegramLocationSettings(),
              retrying: false,
            };
          } else if (!access.allowed && access.locationDiagnostics) {
            issue = { kind: 'failed', diagnostics: access.locationDiagnostics, retrying: false };
          }
          if (!issue) break;

          await askAboutLocation(issue);
          setLocationIssue(current => current && { ...current, retrying: true });
        }
        setLocationIssue(null);
      }

      if (!access.allowed) {
        throw new QueueAccessError(access.code ?? 'unknown', messageFor(access.code, access.message));
      }

      const captchaPassToken = access.captchaRequired ? await solveCaptcha() : undefined;
      await chargerService.joinQueue(chargerId, shift, { location, captchaPassToken });
    },
    [askAboutLocation, messageFor, solveCaptcha]
  );

  const locationModal = locationIssue ? (
    <LocationCheckModal
      issue={locationIssue}
      onOpenSettings={openTelegramLocationSettings}
      onRetry={() => locationDecision.current?.resolve()}
      onClose={() => {
        locationDecision.current?.reject(
          new QueueAccessError('location_check_closed', t('queueAccess.locationPopup.closedHint'))
        );
        locationDecision.current = null;
        setLocationIssue(null);
      }}
    />
  ) : null;

  const captchaModal = pendingCaptcha ? (
    <CaptchaModal
      onSolved={passToken => {
        pendingCaptcha.resolve(passToken);
        setPendingCaptcha(null);
      }}
      onCancel={() => {
        pendingCaptcha.reject(new QueueAccessError('captcha_cancelled', t('captcha.cancelled')));
        setPendingCaptcha(null);
      }}
    />
  ) : null;

  const modals = (
    <>
      {locationModal}
      {captchaModal}
    </>
  );

  return { joinQueue, modals };
}
